import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import generateAvatar from '../../utils/generateAvatar';
import './embed.css';

const EmbedPortfolio = () => {
  const { userId: paramUserId } = useParams();
  const [searchParams] = useSearchParams();

  // URL params
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#0a0a0a' : '#ffffff');
  const userId = paramUserId || searchParams.get('userId');
  const showHeader = searchParams.get('header') !== 'false';
  const showPositions = searchParams.get('positions') !== 'false';
  const showStats = searchParams.get('stats') !== 'false';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;
  const layout = searchParams.get('layout') || 'card'; // card, compact, minimal

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);

  const fetchUserData = async () => {
    if (!userId) {
      setError('No user ID provided');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setError(null);
      } else {
        setError('User not found');
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();

    // Socket for real-time updates
    socketRef.current = io(API_BASE_URL, { auth: { public: true } });

    socketRef.current.on('balance-update', (data) => {
      if (data.userId === userId) {
        setUser(prev => prev ? { ...prev, balance: data.balance } : prev);
      }
    });

    socketRef.current.on('position-update', (data) => {
      if (data.userId === userId) {
        fetchUserData(); // Refresh full data on position changes
      }
    });

    const interval = setInterval(fetchUserData, refreshInterval);

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      clearInterval(interval);
    };
  }, [userId, refreshInterval]);



  const calculateTotalPnL = () => {
    if (!user?.positions) return 0;
    return user.positions.reduce((sum, pos) => sum + (pos.profit || 0), 0);
  };

  const calculatePortfolioValue = () => {
    if (!user) return 0;
    const positionsValue = user.positions?.reduce((sum, pos) => sum + (pos.currentValue || 0), 0) || 0;
    return (user.balance || 0) + positionsValue;
  };

  if (loading) {
    return (
      <div className="embed-container embed-portfolio" style={{ background: bg }}>
        <div className="embed-loading">Loading portfolio...</div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className={`embed-container embed-portfolio ${theme}`} style={{ background: bg === 'transparent' ? 'transparent' : bg }}>
        {showHeader && (
          <div className="portfolio-header">
            <div className="portfolio-user">
              <div className="portfolio-avatar"><span style={{ color: '#555' }}>?</span></div>
              <div className="portfolio-name" style={{ color: '#555' }}>Loading...</div>
            </div>
            <div className="portfolio-value" style={{ color: '#555' }}>--</div>
          </div>
        )}
      </div>
    );
  }

  const totalPnL = calculateTotalPnL();
  const portfolioValue = calculatePortfolioValue();
  const pnlPercent = user.initialBalance ? ((portfolioValue - user.initialBalance) / user.initialBalance * 100) : 0;

  return (
    <div
      className={`embed-container embed-portfolio ${theme} layout-${layout}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {/* Header with user info */}
      {showHeader && (
        <div className="portfolio-header">
          <div className="portfolio-user">
            <div className="portfolio-avatar">
              <img src={user.profilePicture || generateAvatar(user.username)} alt="" className="avatar-img" />
            </div>
            <div className="portfolio-user-info">
              <span className="portfolio-username">{user.username || `Trader ${user.shortId?.slice(0, 4)}`}</span>
              {user.rank && <span className="portfolio-rank">#{user.rank}</span>}
            </div>
          </div>
          <div className="portfolio-live-indicator">
            <span className="live-dot"></span>
            LIVE
          </div>
        </div>
      )}

      {/* Stats */}
      {showStats && (
        <div className="portfolio-stats">
          <div className="stat-item main">
            <span className="stat-label">Portfolio Value</span>
            <span className="stat-value">{formatCurrency(portfolioValue)}</span>
            <span className={`stat-change ${pnlPercent >= 0 ? 'positive' : 'negative'}`}>
              {pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%
            </span>
          </div>

          <div className="stat-row">
            <div className="stat-item">
              <span className="stat-label">Cash</span>
              <span className="stat-value small">{formatCurrency(user.balance || 0)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">P&L</span>
              <span className={`stat-value small ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
                {totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Positions</span>
              <span className="stat-value small">{user.positions?.length || 0}</span>
            </div>
          </div>
        </div>
      )}

      {/* Positions */}
      {showPositions && user.positions && user.positions.length > 0 && (
        <div className="portfolio-positions">
          <div className="positions-header">
            <span>Open Positions</span>
          </div>
          <div className="positions-list">
            {user.positions.map((position, index) => {
              const pnlPercent = position.initialValue
                ? (position.profit / position.initialValue * 100)
                : 0;
              const isLong = position.type === 'LONG';

              return (
                <div key={position.id || index} className="position-item">
                  <div className="position-left">
                    <span className={`position-type ${isLong ? 'long' : 'short'}`}>
                      {isLong ? '▲' : '▼'}
                    </span>
                    <span className="position-ticker">{position.ticker}</span>
                    <span className="position-shares">×{position.shares}</span>
                  </div>
                  <div className="position-right">
                    <span className="position-price">{formatCurrency(position.currentPrice)}</span>
                    <span className={`position-pnl ${position.profit >= 0 ? 'positive' : 'negative'}`}>
                      {position.profit >= 0 ? '+' : ''}{formatCurrency(position.profit)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showPositions && (!user.positions || user.positions.length === 0) && (
        <div className="portfolio-empty">No open positions</div>
      )}
    </div>
  );
};

export default EmbedPortfolio;
